/**
 * ViewServiceAreaDialog
 * Fetches the polygon from S3 via the getServiceAreaPolygon query
 * and renders it on a Google Map.
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, CircularProgress, Alert, Typography,
} from '@mui/material';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

const GET_POLYGON = `
  query GetServiceAreaPolygon($country: String!, $city: String!) {
    getServiceAreaPolygon(country: $country, city: $city) {
      country city displayName polygonCoordinates
    }
  }
`;

interface Props {
  area: { country: string; city: string; displayName?: string } | null;
  onClose: () => void;
}

interface PolygonOverlayProps {
  coordinates: [number, number][];
}

function PolygonOverlay({ coordinates }: PolygonOverlayProps) {
  const map = useMap();
  const polygonRef = useRef<google.maps.Polygon | null>(null);

  useEffect(() => {
    if (!map || !coordinates.length) return;

    const path = coordinates.map(([lng, lat]) => ({ lat, lng }));

    if (polygonRef.current) polygonRef.current.setMap(null);

    const polygon = new google.maps.Polygon({
      paths: path,
      fillColor: '#1976d2',
      fillOpacity: 0.25,
      strokeColor: '#1976d2',
      strokeWeight: 2,
      map,
    });
    polygonRef.current = polygon;

    // Fit map to polygon bounds
    const bounds = new google.maps.LatLngBounds();
    path.forEach(pt => bounds.extend(pt));
    map.fitBounds(bounds, 40);

    return () => { polygon.setMap(null); };
  }, [map, coordinates]);

  return null;
}

export default function ViewServiceAreaDialog({ area, onClose }: Props) {
  const [coords, setCoords] = useState<[number, number][]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!area) return;
    setLoading(true);
    setError(null);
    setCoords([]);
    (async () => {
      try {
        const result: any = await client.graphql({
          query: GET_POLYGON,
          variables: { country: area.country, city: area.city },
        });
        const raw = result.data?.getServiceAreaPolygon?.polygonCoordinates;
        if (raw) {
          // Handle potential double-encoding from the backend
          let parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (typeof parsed === 'string') parsed = JSON.parse(parsed);
          setCoords(parsed);
        } else {
          setError('No polygon data found for this area.');
        }
      } catch (e: any) {
        setError(e.message ?? 'Failed to load polygon');
      } finally {
        setLoading(false);
      }
    })();
  }, [area]);

  return (
    <Dialog open={!!area} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {area?.displayName || `${area?.city}, ${area?.country}`}
        <Typography variant="caption" color="textSecondary" display="block">
          {area?.country} / {area?.city}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        {!loading && !error && (
          <Box sx={{ height: 480 }}>
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
              <Map
                mapId="libride-view-area"
                defaultCenter={{ lat: 6.3156, lng: -10.8074 }}
                defaultZoom={10}
                style={{ width: '100%', height: '100%' }}
                gestureHandling="greedy"
              >
                {coords.length > 0 && <PolygonOverlay coordinates={coords} />}
              </Map>
            </APIProvider>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
